/**
 * Every visible string on the users list, taken verbatim from the legacy resources.
 * Keys are noted so they can be swapped for a real i18n lookup later.
 */
export const strings = {
  /* Fallbacks for an action outcome. The server almost always sends its own
     sentence — a duplicate username, the policy a password missed — and that is
     what gets shown; these cover the case where it sends none. */
  actionDone: 'تمت العملية بنجاح',
  actionFailed: 'تعذّر إتمام العملية، فضلا حاول مرة أخرى',
  // Admin/Users.ar.resx
  title: 'قائمة المستخدمين', // PageResource1.Title
  subtitle: 'قم باختيار مستخدم من اجل تعديل بيانات ملفه الشخصي', // MainPanelSubHeader1

  tabs: {
    ACTIVE: 'المفعلين', // Resources.Active
    SUSPENDED: 'الموقوفين', // Resources.Suspended
    WAITING_ACTIVATION_APPROVAL: 'الانتظار', // Resources.Waiting_Activation
    IDEAL_USERS: 'لم يستخدموا النظام', // Resources.LabelInActiveUsers
  },

  columns: {
    index: 'م', // Users.UsersList_HeaderText1
    userName: 'اسم المستخدم', // Users.UsersList_HeaderText2
    name: 'الاسم الكامل', // General.FullNameLabel
    email: 'البريد الإلكتروني', // Users.UsersList_HeaderText5
    groups: 'الإدارة', // Users.UsersList_HeaderText6
    status: 'الحاله', // Resources.Status
  },

  actions: {
    add: 'إضافة', // Resources.Add
    createLocalUser: 'إنشاء مستخدم (محلي)', // Resources.CreateLocalUser
    importFromExcelLocal: 'إستيراد من ملف إكسل (محلي)', // Resources.ImportFromExcelLocal
    importFromExcelAd: 'إستيراد من ملف إكسل (مجلد نشط)', // Resources.ImportFromExcelActiveDirectory
    update: 'تحديث', // Resources.Update
    syncDataSource: 'سحب وتزامن البيانات', // Resources.Fetch_and_synchronize + Resources.Data
    importResigned: 'تحديث بيانات المستقيلين من ملف اكسل', // Resources.UpdateTheDataOfResigned
    resetPasswords: 'إعادة تعيين كلمات مرور المستخدمين', // Resources.ResetPasswordForUsers
    filter: 'بحث', // Resources.Filter
  },

  /** سحب المستخدمين من مصدر الربط — `/Users/Clone`. */
  clone: {
    title: 'سحب المستخدمين من مصدر الربط',
    hint: 'إضافة المستخدمين الموجودين في مصدر الربط إلى قائمة المستخدمين',
    source: 'مصدر الربط',
    sourcePlaceholder: 'اختر مصدر الربط',
    note: 'تتم إضافة المستخدمين الجدد فقط؛ لا يتم حذف أو تعديل أي مستخدم موجود.',
    submit: 'سحب المستخدمين',
    running: 'فضلا انتظر',
  },

  /** جلب بيانات المستخدم من مصدر الربط — `/Users/GetUserInfoFromDataSource`. */
  fetch: {
    action: 'جلب البيانات من مصدر الربط',
    hint: 'اكتب اسم المستخدم ثم اجلب بياناته من مصدر الربط لتعبئة الحقول',
    running: 'جاري الجلب',
    usernameRequired: 'اكتب اسم المستخدم أولاً',
    filled: 'تم جلب بيانات المستخدم',
    notFound: 'لم يتم العثور على هذا المستخدم في مصدر الربط',
  },

  filter: {
    groupLabel: 'الإدارات:', // Resources.Groups
    groupPlaceholder: 'الإدارة', // data-placeholder
    typeLabel: 'النوع:', // Resources.Type
    typePlaceholder: 'نوع المستخدم', // data-placeholder
    adminsOnly: 'مدراء النظام فقط', // Resources.AdminsOnly
    usersOnly: 'المستخدمين فقط', // Resources.UsersOnly
    cancel: 'إلغاء', // Resources.Reset
    submit: 'بحث', // Resources.Filter
  },

  row: {
    edit: 'تعديل البيانات الشخصية', // Users.btnEdit — the legacy reuses this for both row buttons
    /* The lock button opened the status dialog under the SAME label as the pencil beside
       it, so the two buttons announced themselves identically. */
    status: 'حالة الحساب',
    delete: 'حذف المستخدم', // Users.btnDelete
    /* Deleting is irreversible — `DeleteUserHandler` removes the identity user outright —
       so the dialog says what goes, not just "are you sure". */
    deleteConfirm: (name) =>
      `سيتم حذف حساب ${name} نهائيًا مع صلاحياته وسجل دخوله. لا يمكن التراجع عن هذه العملية.`,
  },

  /* The two export labels and their failure message went with the export menu
     itself, which this screen no longer offers. */
  footer: {
    deleteInactive: 'حذف', // Groups.btnDelete
  },

  /** #add_modal — mode A, `showCreationForm(false)` */
  create: {
    title: 'إنشاء مستخدم (محلي)', // Resources.CreateLocalUser
    hint: 'هذه الخاصية تستخدم لتسجيل مستخدم محلي بدون الربط مع الاكتف دايكتري',
    username: 'اسم المستخدم', // Users.UserNameLabelResource1_Text
    password: 'كلمة المرور', // Users.PasswordLabelResource1_Text
    confirmPassword: 'تأكيد كلمة المرور', // Users.ConfirmPasswordLabelResource1_Text
    email: 'البريد الإلكتروني', // Users.EmailLabelResource1_Text
    submit: 'إنشاء حساب مستخدم', // Sitemap.CreateUser_Title
    errors: {
      usernameRequired: 'اسم المستخدم مطلوب.',
      passwordRequired: 'كلمة المرور مطلوبة',
      passwordFormat:
        'كلمة المرور يجب أن تكون ما بين 6-64 حرف متضمنة على الأقل رقم واحد وأحد الرموز !@#$%^&*',
      confirmRequired: 'تأكيد كلمة المرور مطلوبة',
      confirmMismatch: 'تأكيد كلمة المرور لا يتطابق مع كلمة المرور',
      emailRequired: 'البريد الإلكتروني مطلوب',
      emailFormat: 'من فضلك قم ادخال بريد الكتروني صحيح',
    },
  },

  /** #add_modal — mode B, `showCreationForm(true, type)` */
  importModal: {
    intro: 'يُرجى رفع ملف بصيغة Excel (.xlsx) أو CSV (.csv)', // Resources.Please_upload_a_file_in_Excel
    columnsIntro: 'يجب أن يحتوي الملف على الأعمدة التالية',
    mandatory: 'إجباري', // Resources.Mandatory
    optional: 'إختياري', // Resources.Optional
    notesTitle: 'ملاحظات مهمة',
    notes:
      'يجب أن يحتوي الصف الأول في الملف على أسماء الأعمدة، وبنفس الأسماء والترتيب الموضح في الجدول',
    template: 'معاينة القالب', // Resources.ViewTemplate
    save: 'حفظ', // Resources.Save
    fileError: 'ملف إكسل او CSV', // Resources.ExcelOrCSV
  },

  /** Per-type configuration for the excel modal — js.cshtml:489-538. */
  importTypes: {
    UsersByEmail: {
      title: 'إستيراد من ملف إكسل (محلي)', // Resources.ImportFromExcelLocal
      hint: 'هذه الخاصية تستخدم لتسجيل عدة مستخدمين محلّيين من خلال ملف الإكسل بدون ربط مع الاكتف دايركتوري',
      columns: [
        { name: 'Email', required: true },
        { name: 'FullName', required: false },
        { name: 'Department', required: false },
      ],
    },
    ActiveDirectoryUsers: {
      title: 'إستيراد من ملف إكسل (مجلد نشط)', // Resources.ImportFromExcelAD
      hint: 'هذه الخاصية تستخدم لتسجيل عدة مستخدمين من خلال ملف الإكسل بشرط تواجد اسم المستخدم في الاكتف دايركتري حتى يتم تسجيله، وهي تستخدم للجهات التي بها الاكتف دايركتري غير محدث أو لا يمكن تشغيل التزامن',
      columns: [
        { name: 'Username', required: true },
        { name: 'Email', required: true },
        { name: 'Department Name', required: true },
        { name: 'Full Name', required: true },
      ],
    },
    ResignedUsers: {
      title: 'تحديث بيانات المستقيلين من ملف اكسل', // Resources.UpdateTheDataOfResigned
      hint: 'تستخدم هذه الخاصية لتحديث الاستقالة بشكل يدوي (تستخدم فقط في حال لم يتم تفعيل التزامن وتفعيل اكتشاف الاستقالات من مصدر الربط)',
      columns: [
        { name: 'Username', required: true },
        { name: 'Resignation Date (d/M/yyyy)', required: true },
      ],
    },
    ResetUsersPasswords: {
      title: 'إعادة تعيين كلمات مرور المستخدمين', // Resources.ResetPasswordForUsers
      hint: 'يمكن استخدام الخاصية لإعادة تعيين كلمات المرور للمستخدمين الحاليين',
      columns: [{ name: 'Username', required: true }],
    },
  },

  /** #edit_modal */
  edit: {
    title: 'تعديل البيانات الشخصية', // Users.btnEdit
    tabs: {
      profile: 'تعديل البيانات الشخصية',
      permissions: 'الصلاحيات',
      password: 'تغيير كلمة المرور', // Users.PageResource1_Title4
    },
    nameAr: 'الاسم بالعربية', // Users.SpanNameAr
    nameEn: 'الاسم بالإنجليزية', // Users.SpanNameEn
    group: 'الإدارة', // Users.SpanGroup
    gender: 'الجنس', // UserProfile.Label3Resource1_Text
    selectGender: 'اختر الجنس', // Resources.Select_Gender
    male: 'ذكر',
    female: 'أنثى',
    email: 'البريد الإلكتروني', // UserProfile.Label4Resource1_Text
    phone: 'رقم الهاتف', // UserProfile.Label5Resource1_Text
    mobile: 'رقم الجوال', // UserProfile.Label6Resource1_Text
    employeeId: 'رقم الموظف', // UserProfile.LabelEmployeeIdResource1_Text
    save: 'حفظ', // Resources.Save
    required: 'الحقل مطلوب', // Users.RequiredFieldValidator
    lengthError: 'المدخل لابد ألا يقل عن 3 أحرف و لا يزيد عن 200',
    selectAllRoles: 'اختيار الكل', // Users.SelectAll
    updateRoles: 'تعديل', // Users.BtnUpdateData_Text
    roleUser: 'مستخدم عادي', // Resources.User
    roleAdmin: 'مدير النظام', // Resources.Admin
    autoPasswordHint: 'تغيير كلمة المرور تلقائياً واراسلها لبريد المستخدم',
    autoPasswordButton: 'غير كلمة المرور', // Resources.Change_user_password
    manualPasswordHint: 'وضع كلمة المرور يدوياً',
    newPassword: 'كلمة المرور',
    confirmNewPassword: 'تأكيد كلمة المرور',
    changePassword: 'تغيير كلمة المرور', // Users.PageResource1_Title4
    passwordMismatch: 'كلمة المرور لا تتطابق مع تأكيد كلمة المرور',
    /* Were hardcoded in the drawer's JSX; here so the whole tab reads from one
       place like every other string on this screen. */
    noPasswordChange: 'لا يمكن تغيير كلمة المرور',
    noPasswordChangeReason: 'لا يمكن تغيير كلمة المرور اذا كان نظام الدخول عبر المجلد النشط',
  },

  /** #edit_status_modal */
  status: {
    tabs: {
      approve: 'حالة الحساب',
      expiry: 'تاريخ انتهاء الحساب', // Resources.Account_Expiry
      locked: 'تعطيل الحساب لفترة',
      resigned: 'الإستقالة',
    },
    approved: 'نشط الحساب', // Users.ApproveCheckBoxResource1_Text
    notes: 'ملاحظات', // Resources.Notes
    update: 'تعديل', // Users.BtnUpdateData_Text
    save: 'حفظ', // Resources.Save
    expiryLabel: 'تاريخ انتهاء الحساب', // Resources.Account_Expiry
    lockoutLabel: 'تاريخ نهاية الايقاف', // Resources.Lockout_End_Date
    /* What an EMPTY value does, on the two fields where it is an instruction
       rather than a blank — the clear button is how you get there. */
    lockoutHint: 'اترك الحقل فارغًا لرفع الإيقاف عن الحساب',
    /* Lifting a lockout is its own endpoint: it also resets the failed-attempt counter. */
    unlockNow: 'رفع الإيقاف الآن',
    fetchedResignation: 'تاريخ الاستقاله المسحوب من عمليه التزامن',
    none: 'لا يوجد',
    resignationLabel: 'تاريخ الاستقاله', // Resources.Resignation_Date
    resignationHint: 'اترك الحقل فارغًا لإلغاء الاستقالة',
  },

  deleteInactiveConfirm: 'هل أنت متأكد أنك تريد حذف المستخدمين اللذين لم يستخدموا النظام؟', // Resources.Are_you_sure_you_want_to_delete_the_inactive_users_
  selectAll: 'تحديد الكل', // Resources.Select_All
  close: 'إغلاق', // Users.btnClose
}
